import {getTestProps} from '@benchling/js-test-utils';
import {createStyles} from '@mantine/core';
import React, {useState} from 'react';

import {ChatRenderContext} from 'llm/chat-library/ChatRenderContext';
import {
  BINARY_FILE_PREVIEW_MESSAGE,
  isTextPreviewablePath,
  SKILL_MD_PATH,
} from 'llm/chat-library/shared/SkillFileUtils';
import {parseSkillFrontmatter} from 'llm/chat-library/tool-details/ManageSkillToolCallParser';
import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import MonacoEditor from 'llm/monaco/MonacoEditor';
import {MonacoLanguage} from 'llm/monaco/MonacoTypes';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';

const MIN_EDITOR_HEIGHT = 120;
const MAX_EDITOR_HEIGHT = 320;
const BORDER_OFFSET = 2;

const previewRootTestProps = getTestProps({component: 'SkillFileContentBlocks', element: 'previewRoot'});

const useContentStyles = createStyles((theme) => ({
  codeBlock: {
    ...FONT_PROXIMA_12,
    color: theme.colors.gray[7],
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray[0],
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    minWidth: 0,
    fontFamily: theme.fontFamilyMonospace,
    lineHeight: 1.4,
  },
  editorContainer: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  markdownContainer: {
    ...FONT_PROXIMA_14,
    padding: `0 ${theme.spacing.md}`,
    backgroundColor: theme.white,
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    overflowWrap: 'anywhere',
    minWidth: 0,
  },
  skillMdBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.white,
    padding: theme.spacing.md,
  },
  frontmatterBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    paddingBottom: theme.spacing.sm,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
  },
  frontmatterField: {
    ...FONT_PROXIMA_14,
    color: theme.colors.gray[7],
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    alignItems: 'baseline',
  },
  frontmatterLabel: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    flexShrink: 0,
  },
  frontmatterValue: {
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
}));

export function MonoBlock({text, className}: {text: string; className?: string}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useContentStyles();
  return (
    <div {...previewRootTestProps} className={cx(classes.codeBlock, className)}>
      {text}
    </div>
  );
}

export function ReadOnlyCodeBlock({
  code,
  language,
  className,
}: {
  code: string;
  language: MonacoLanguage;
  className?: string;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useContentStyles();
  const {isExportRender} = ChatRenderContext.useContext();
  const [height, setHeight] = useState(MIN_EDITOR_HEIGHT);

  if (isExportRender) {
    return (
      <MonacoEditor
        value={code}
        language={language}
        height="auto"
        width="100%"
        theme="vs"
        options={{readOnly: true, minimap: {enabled: false}, scrollBeyondLastLine: false, wordWrap: 'on'}}
      />
    );
  }

  return (
    <div {...previewRootTestProps} className={cx(className, classes.editorContainer)} style={{height}}>
      <MonacoEditor
        value={code}
        language={language}
        height="100%"
        width="100%"
        theme="vs"
        options={{readOnly: true, minimap: {enabled: false}, scrollBeyondLastLine: false, wordWrap: 'on'}}
        onChangeContentSize={(e) => {
          setHeight(
            Math.min(Math.max(MIN_EDITOR_HEIGHT, e.contentHeight + BORDER_OFFSET), MAX_EDITOR_HEIGHT)
          );
        }}
      />
    </div>
  );
}

export function MarkdownBlock({markdown, className}: {markdown: string; className?: string}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useContentStyles();
  return (
    <div {...previewRootTestProps} className={cx(classes.markdownContainer, className)}>
      <LLMMarkdownSection markdown={markdown} />
    </div>
  );
}

function SkillMdBlock({content, className}: {content: string; className?: string}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useContentStyles();
  const {name, description, body} = parseSkillFrontmatter(content);
  if (name == null && description == null) {
    return <MarkdownBlock markdown={body} className={className} />;
  }
  return (
    <div {...previewRootTestProps} className={cx(classes.skillMdBlock, className)}>
      <div className={classes.frontmatterBlock}>
        {name != null && (
          <div className={classes.frontmatterField}>
            <span className={classes.frontmatterLabel}>Name:</span>
            <span className={classes.frontmatterValue}>{name}</span>
          </div>
        )}
        {description != null && (
          <div className={classes.frontmatterField}>
            <span className={classes.frontmatterLabel}>Description:</span>
            <span className={classes.frontmatterValue}>{description}</span>
          </div>
        )}
      </div>
      {body.trim().length > 0 && <LLMMarkdownSection markdown={body} />}
    </div>
  );
}

type SupportedLanguage = 'python' | 'sql' | 'json';

interface FileExtensionConfig {
  extension: string;
  language: SupportedLanguage;
}

const CODE_FILE_EXTENSIONS: Array<FileExtensionConfig> = [
  {extension: '.py', language: 'python'},
  {extension: '.sql', language: 'sql'},
  {extension: '.json', language: 'json'},
];

export interface SkillFileContentRendererProps {
  virtualPath: string;
  content: string;
  className?: string;
}

export function SkillFileContentRenderer({
  virtualPath,
  content,
  className,
}: SkillFileContentRendererProps): JSX.Element {
  if (!isTextPreviewablePath(virtualPath)) {
    return <MonoBlock text={BINARY_FILE_PREVIEW_MESSAGE} className={className} />;
  }

  const lowerPath = virtualPath.toLowerCase();

  if (virtualPath === SKILL_MD_PATH) {
    return <SkillMdBlock content={content} className={className} />;
  }

  if (lowerPath.endsWith('.md')) {
    return <MarkdownBlock markdown={content} className={className} />;
  }

  for (const {extension, language} of CODE_FILE_EXTENSIONS) {
    if (lowerPath.endsWith(extension)) {
      return <ReadOnlyCodeBlock code={content} language={language} className={className} />;
    }
  }

  return <MonoBlock text={content} className={className} />;
}
